/**
 * AUTH CONTEXT
 *
 * Manages authentication state and license verification
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import apiService from "../services/apiService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = Cookies.get("auth_token");
      console.log("🔍 Auth Check - Token found:", !!token);

      if (!token) {
        console.log("❌ No token found, user not authenticated");
        setIsLoading(false);
        return;
      }

      console.log("🚀 Fetching license info...");

      // Verify token with backend
      const response = await apiService.getLicenseInfo();
      console.log("📊 License API Response:", response.data);

      if (response.data.success) {
        const licenseData = response.data.data;
        console.log("✅ License data received:", licenseData);
        console.log(
          "Raw API response:",
          JSON.stringify(response.data, null, 2),
        );
        console.log("License valid:", licenseData.isValid);
        console.log("License expired:", licenseData.isExpired);
        console.log("Days remaining:", licenseData.daysRemaining);

        // Ensure boolean values are properly mapped
        const userData = {
          ...licenseData,
          isValid: Boolean(licenseData.isValid),
          isExpired: Boolean(licenseData.isExpired),
        };

        console.log("📝 Processed user data:", userData);
        console.log("Final isValid:", userData.isValid);
        console.log("Final isExpired:", userData.isExpired);

        setUser(userData);
        setIsAuthenticated(true);
      } else {
        console.log("❌ License API returned error:", response.data);
        // Invalid token
        logout();
      }
    } catch (error) {
      console.error("❌ Auth check failed:", error);
      console.error("Error response:", error.response?.data);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setIsLoading(true);

      console.log("🚀 Login Debug - Starting login process...");
      console.log("📧 Credentials:", credentials);

      const response = await apiService.login(credentials);

      console.log("📊 Login Response:", response);
      console.log("📊 Response Data:", response.data);

      if (response.data.success) {
        const { token, user } = response.data.data;

        console.log("✅ Login Success - Setting auth state...");
        console.log(
          "🎯 Token:",
          typeof token === "string" ? token.substring(0, 50) + "..." : token,
        );
        console.log("👤 User:", user);

        // Store token in cookie
        Cookies.set("auth_token", token, {
          expires: 7, // 7 days
          secure: process.env.NODE_ENV === "production",
          sameSite: "strict",
        });

        // Set authorization header
        // Set authorization header is handled by apiService

        setUser(user);
        setIsAuthenticated(true);

        toast.success(`Welcome back, ${user.username}!`);

        return { success: true };
      } else {
        console.log("❌ Login Failed - Server response:", response.data);
        toast.error(response.data.message || "Login failed");
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      console.error("❌ Login Error Details:", error);
      console.error("Error Response:", error.response);
      console.error("Error Message:", error.message);
      console.error("Error Code:", error.code);

      const errorMessage =
        error.response?.data?.message || error.message || "Login failed";

      console.log("🔥 Final Error Message:", errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);

      console.log("🚀 Registration Debug:");
      console.log("User Data:", userData);
      console.log("Calling apiService.register...");

      const response = await apiService.register(userData);

      console.log("✅ Registration Response:", response);
      console.log("Response Data:", response.data);

      if (response.data.success) {
        const { token, user } = response.data.data;

        // Store token in cookie
        Cookies.set("auth_token", token, {
          expires: 7,
          secure: process.env.NODE_ENV === "production",
          sameSite: "strict",
        });

        // Set authorization header is handled by apiService

        setUser(user);
        setIsAuthenticated(true);

        console.log(
          "🎯 Token (register):",
          typeof token === "string" ? token.substring(0, 50) + "..." : token,
        );

        toast.success(
          `Welcome, ${user.username}! Your trial license is active.`,
        );

        return { success: true };
      } else {
        console.log("❌ Registration Failed - Response:", response.data);
        toast.error(response.data.message || "Registration failed");
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      console.error("❌ Registration Error Details:", error);
      console.error("Error Response:", error.response);
      console.error("Error Message:", error.message);
      console.error("Error Code:", error.code);

      const errorMessage =
        error.response?.data?.message || error.message || "Registration failed";

      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Remove token from cookie
    Cookies.remove("auth_token");

    // Authorization header removal is handled by apiService

    setUser(null);
    setIsAuthenticated(false);

    toast.success("Logged out successfully");
  };

  const updateUser = (userData) => {
    setUser((prevUser) => ({
      ...prevUser,
      ...userData,
    }));
  };

  const refreshUser = async () => {
    try {
      const response = await apiService.getLicenseInfo();

      if (response.data.success) {
        setUser(response.data.data);
        return response.data.data;
      }
    } catch (error) {
      console.error("Refresh user failed:", error);
    }
  };

  const verifyLicense = async (licenseKey) => {
    try {
      const response = await apiService.verifyLicense(licenseKey);

      return response.data;
    } catch (error) {
      console.error("License verification failed:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Verification failed",
      };
    }
  };

  const checkActionPermission = async (action) => {
    try {
      const response = await apiService.checkPermission(action);

      return response.data;
    } catch (error) {
      console.error("Permission check failed:", error);
      return { success: false, canPerform: false };
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    verifyLicense,
    checkActionPermission,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
